import React from "react";
import { NavLink, useNavigate, Outlet, useLocation } from "react-router-dom";
import { LayoutDashboard, FileText, Zap, Repeat, FolderOpen, Users, Building2, MessageSquare, FileEdit, Settings, LogOut, Home, Handshake, Mail, Landmark, Gift, Award, PhoneCall, UserCog } from "lucide-react";
import { Logo } from "@/components/site/Logo";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

const links = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true, processor: true },
  { to: "/admin/intake", label: "New Phone Deal", icon: PhoneCall, processor: true },
  { to: "/admin/loan", label: "Loan Applications", icon: FileText, processor: true },
  { to: "/admin/emd", label: "EMD Requests", icon: Zap, processor: true },
  { to: "/admin/transactional", label: "Transactional", icon: Repeat, processor: true },
  { to: "/admin/documents", label: "Documents", icon: FolderOpen, processor: true },
  { to: "/admin/borrowers", label: "Borrowers", icon: Users, admin: true },
  { to: "/admin/partners", label: "Capital Partners", icon: Building2, admin: true },
  { to: "/admin/lenders", label: "Lenders", icon: Landmark },
  { to: "/admin/preapprovals", label: "Pre-Approvals", icon: Award, processor: true },
  { to: "/admin/referrals", label: "Referrals", icon: Handshake, admin: true },
  { to: "/admin/referral-partners", label: "Referral Partners", icon: Gift, admin: true },
  { to: "/admin/contacts", label: "Inquiries", icon: Mail, admin: true },
  { to: "/admin/messages", label: "Messages", icon: MessageSquare, processor: true },
  { to: "/admin/team", label: "Team", icon: UserCog, admin: true },
  { to: "/admin/content", label: "Website Content", icon: FileEdit },
  { to: "/admin/settings", label: "Settings", icon: Settings },
];

const visibleLinks = (role) => links.filter((l) => (role === "admin" ? true : role === "processor" ? l.processor : !l.admin));

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const allowed = visibleLinks(user?.role).some((l) => (l.end ? pathname === l.to || pathname === `${l.to}/` : pathname.startsWith(l.to)));
  return (
    <div className="min-h-screen bg-obsidian flex flex-col lg:flex-row">
      <aside className="lg:w-64 lg:min-h-screen border-b lg:border-b-0 lg:border-r border-edge bg-charcoal/60 flex lg:flex-col">
        <div className="p-5 border-b border-edge flex items-center justify-between">
          <Logo size={34} showText={false} />
          <span className="text-[10px] font-bold uppercase tracking-widest text-gold border border-gold/40 rounded px-1.5 py-0.5">{user?.role === "processor" ? "Processor" : user?.role === "loan_officer" ? "Loan Officer" : "Admin"}</span>
        </div>
        <nav className="flex lg:flex-col gap-0.5 p-3 overflow-x-auto flex-1">
          {visibleLinks(user?.role).map((l) => (
            <NavLink key={l.to} to={l.to} end={l.end} data-testid={`admin-nav-${l.label.replace(/\s/g, "-").toLowerCase()}`}
              className={({ isActive }) => `flex items-center gap-3 px-3.5 py-2.5 rounded-lg text-sm font-semibold whitespace-nowrap transition-colors ${isActive ? "bg-gold/15 text-gold" : "text-white/70 hover:bg-surface hover:text-white"}`}>
              <l.icon className="w-4.5 h-4.5 w-5 h-5" /> {l.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-edge hidden lg:block">
          <div className="px-3.5 py-2 text-xs text-faint truncate">{user?.email} · {user?.role}</div>
          <button onClick={() => navigate("/")} className="flex items-center gap-3 px-3.5 py-2 rounded-lg text-sm text-white/60 hover:text-gold w-full"><Home className="w-4 h-4" /> Public Site</button>
          <button onClick={logout} className="flex items-center gap-3 px-3.5 py-2 rounded-lg text-sm text-white/60 hover:text-brandred w-full" data-testid="admin-logout"><LogOut className="w-4 h-4" /> Sign Out</button>
        </div>
      </aside>
      <div className="flex-1 min-w-0">
        <header className="flex items-center justify-between px-5 py-4 border-b border-edge lg:hidden">
          <Logo size={30} showText={false} />
          <Button variant="ghost" size="sm" onClick={logout} className="text-white/60"><LogOut className="w-4 h-4" /></Button>
        </header>
        <main className="p-5 sm:p-8">{allowed ? <Outlet context={{ user }} /> : (
          <div className="py-20 text-center" data-testid="admin-no-access">
            <h1 className="h-display text-2xl text-white">No access</h1>
            <p className="text-dim mt-2">This page is for admins only.</p>
            <Button onClick={() => navigate("/admin")} className="btn-gold rounded-lg mt-5">Back to Dashboard</Button>
          </div>)}</main>
      </div>
    </div>
  );
}
